import { createBrowserRouter, type RouteObject } from 'react-router-dom';
import type { QueryClient } from '@tanstack/react-query';
import { IntegrationsRedirect, RootRedirect, WorkspaceRoute } from '@/app/WorkspaceRoute';
import { RouteError } from '@/app/RouteError';
import { NotConfigured } from '@/app/NotConfigured';
import { getBrand } from '@/lib/api/endpoints';
import { queryKeys } from '@/lib/queryKeys';
import { env } from '@/lib/env';

/**
 * Route table.
 *
 * Every screen is code-split. The brand routes carry a loader that resolves the
 * brand through the same query cache the screen will read, so the guard and the
 * screen can never disagree about whether the brand exists, and the screen does
 * not re-fetch what the loader already proved (design doc §22.1).
 */
export function buildRoutes(queryClient: QueryClient): RouteObject[] {
  const brandLoader = async ({
    params,
  }: {
    params: { workspaceId?: string; brandId?: string };
  }) => {
    const { workspaceId, brandId } = params;
    if (!workspaceId || !brandId) throw new Response('Not Found', { status: 404 });
    return queryClient.ensureQueryData({
      queryKey: queryKeys.brand(workspaceId, brandId),
      queryFn: ({ signal }) => getBrand(workspaceId, brandId, signal),
    });
  };

  return [
    { path: '/', element: <RootRedirect /> },
    { path: '/not-configured', element: <NotConfigured /> },
    // The OAuth callback's landing for a dead link (ADR-056).
    { path: '/integrations', element: <IntegrationsRedirect /> },
    // The mock OAuth provider's consent page (ADR-043): only when the mock
    // service worker runs, gated exactly as `main.tsx` gates it, so a
    // production build has neither the route nor its chunk.
    ...(env.mswEnabled && import.meta.env.DEV
      ? [
          {
            path: '/dev/oauth/:provider/authorize',
            lazy: async () => ({
              Component: (await import('@/features/integrations/mock-oauth/MockConsentScreen'))
                .MockConsentScreen,
            }),
            errorElement: <RouteError />,
          },
        ]
      : []),
    {
      // After sign-out (ADR-014); needs no session.
      path: '/signed-out',
      lazy: async () => ({
        Component: (await import('@/app/auth/SignedOutScreen')).SignedOutScreen,
      }),
    },
    {
      // Switch or create a workspace (GET /me, GET|POST /workspaces).
      path: '/workspaces',
      lazy: async () => ({
        Component: (await import('@/features/settings/WorkspacePickerScreen'))
          .WorkspacePickerScreen,
      }),
      errorElement: <RouteError />,
    },
    {
      path: '/w/:workspaceId',
      element: <WorkspaceRoute />,
      errorElement: <RouteError />,
      children: [
        { index: true, element: <RootRedirect /> },
        {
          path: 'portfolio',
          lazy: async () => ({
            Component: (await import('@/features/portfolio/PortfolioScreen')).PortfolioScreen,
          }),
          errorElement: <RouteError />,
        },
        {
          path: 'brands',
          lazy: async () => ({
            Component: (await import('@/features/brands/BrandListScreen')).BrandListScreen,
          }),
          errorElement: <RouteError />,
        },
        {
          path: 'brands/:brandId',
          loader: brandLoader,
          lazy: async () => ({
            Component: (await import('@/features/brands/BrandWorkspaceScreen'))
              .BrandWorkspaceScreen,
          }),
          errorElement: <RouteError />,
          children: [
            {
              index: true,
              lazy: async () => ({
                Component: (await import('@/features/brands/BrandOverviewTab')).BrandOverviewTab,
              }),
            },
            {
              path: 'facts',
              lazy: async () => ({
                Component: (await import('@/features/facts/FactVaultScreen')).FactVaultScreen,
              }),
              errorElement: <RouteError />,
            },
            {
              // A sibling of the vault rather than a tab: design doc §5.3 fixes
              // the eight brand tabs, and the queue is a mode of the Fact Vault
              // rather than a ninth section of the workspace.
              path: 'facts/review',
              lazy: async () => ({
                Component: (await import('@/features/facts/FactReviewQueueScreen'))
                  .FactReviewQueueScreen,
              }),
              errorElement: <RouteError />,
            },
            {
              path: 'sources',
              lazy: async () => ({
                Component: (await import('@/features/sources/SourcesScreen')).SourcesScreen,
              }),
              errorElement: <RouteError />,
            },
            {
              path: 'audiences',
              lazy: async () => ({
                Component: (await import('@/features/brands/AudiencesTab')).AudiencesTab,
              }),
              errorElement: <RouteError />,
            },
            {
              path: 'voice',
              lazy: async () => ({
                Component: (await import('@/features/brands/BrandBookTab')).BrandBookTab,
              }),
              errorElement: <RouteError />,
            },
            {
              path: 'offers',
              lazy: async () => ({
                Component: (await import('@/features/brands/OffersTab')).OffersTab,
              }),
              errorElement: <RouteError />,
            },
            {
              path: 'policies',
              lazy: async () => ({
                Component: (await import('@/features/brands/PoliciesTab')).PoliciesTab,
              }),
              errorElement: <RouteError />,
            },
          ],
        },
        {
          path: 'approvals',
          lazy: async () => ({
            Component: (await import('@/features/approvals/ApprovalsInboxScreen'))
              .ApprovalsInboxScreen,
          }),
          errorElement: <RouteError />,
        },
        {
          path: 'approvals/:approvalId',
          lazy: async () => ({
            Component: (await import('@/features/approvals/ApprovalDecisionScreen'))
              .ApprovalDecisionScreen,
          }),
          errorElement: <RouteError />,
        },
        {
          path: 'publications',
          lazy: async () => ({
            Component: (await import('@/features/publications/PublicationsListScreen'))
              .PublicationsListScreen,
          }),
          errorElement: <RouteError />,
        },
        {
          path: 'publications/:publicationId',
          lazy: async () => ({
            Component: (await import('@/features/publications/PublicationScreen'))
              .PublicationScreen,
          }),
          errorElement: <RouteError />,
        },
        {
          path: 'campaigns',
          lazy: async () => ({
            Component: (await import('@/features/campaigns/CampaignListScreen')).CampaignListScreen,
          }),
          errorElement: <RouteError />,
        },
        {
          path: 'campaigns/new',
          lazy: async () => ({
            Component: (await import('@/features/campaigns/builder/CampaignBuilderScreen'))
              .CampaignBuilderScreen,
          }),
          errorElement: <RouteError />,
        },
        {
          path: 'campaigns/:campaignId',
          lazy: async () => ({
            Component: (await import('@/features/campaigns/detail/CampaignDetailScreen'))
              .CampaignDetailScreen,
          }),
          errorElement: <RouteError />,
        },
        {
          path: 'content',
          lazy: async () => ({
            Component: (await import('@/features/content/ContentListScreen')).ContentListScreen,
          }),
          errorElement: <RouteError />,
        },
        {
          path: 'content/:itemId',
          lazy: async () => ({
            Component: (await import('@/features/content/ContentReviewScreen')).ContentReviewScreen,
          }),
          errorElement: <RouteError />,
        },
        {
          path: 'calendar',
          lazy: async () => ({
            Component: (await import('@/features/calendar/CalendarScreen')).CalendarScreen,
          }),
          errorElement: <RouteError />,
        },
        {
          path: 'seo',
          lazy: async () => ({
            Component: (await import('@/features/seo/SeoScreen')).SeoScreen,
          }),
          errorElement: <RouteError />,
        },
        {
          path: 'experiments',
          lazy: async () => ({
            Component: (await import('@/features/experiments/ExperimentsScreen')).ExperimentsScreen,
          }),
          errorElement: <RouteError />,
        },
        {
          path: 'experiments/new',
          lazy: async () => ({
            Component: (await import('@/features/experiments/ExperimentCreateScreen'))
              .ExperimentCreateScreen,
          }),
          errorElement: <RouteError />,
        },
        {
          path: 'experiments/:experimentId',
          lazy: async () => ({
            Component: (await import('@/features/experiments/ExperimentDetailScreen'))
              .ExperimentDetailScreen,
          }),
          errorElement: <RouteError />,
        },
        {
          path: 'campaigns/:campaignId/analytics',
          lazy: async () => ({
            Component: (await import('@/features/analytics/CampaignAnalyticsScreen'))
              .CampaignAnalyticsScreen,
          }),
          errorElement: <RouteError />,
        },
        {
          path: 'recommendations',
          lazy: async () => ({
            Component: (await import('@/features/recommendations/RecommendationsScreen'))
              .RecommendationsScreen,
          }),
          errorElement: <RouteError />,
        },
        {
          path: 'analytics',
          lazy: async () => ({
            Component: (await import('@/features/analytics/AnalyticsScreen')).AnalyticsScreen,
          }),
          errorElement: <RouteError />,
        },
        {
          path: 'integrations',
          lazy: async () => ({
            Component: (await import('@/features/integrations/IntegrationsScreen'))
              .IntegrationsScreen,
          }),
          errorElement: <RouteError />,
        },
        {
          path: 'settings',
          lazy: async () => ({
            Component: (await import('@/features/settings/SettingsScreen')).SettingsScreen,
          }),
          errorElement: <RouteError />,
          children: [
            {
              index: true,
              lazy: async () => ({
                Component: (await import('@/features/settings/GeneralSettingsTab'))
                  .GeneralSettingsTab,
              }),
            },
            {
              path: 'audit',
              lazy: async () => ({
                Component: (await import('@/features/settings/AuditLogTab')).AuditLogTab,
              }),
            },
            {
              path: 'cost',
              lazy: async () => ({
                Component: (await import('@/features/settings/CostTab')).CostTab,
              }),
            },
            {
              path: 'agent-runs',
              lazy: async () => ({
                Component: (await import('@/features/settings/AgentRunsTab')).AgentRunsTab,
              }),
            },
            {
              path: 'agent-runs/:runId',
              lazy: async () => ({
                Component: (await import('@/features/settings/AgentRunDetailScreen'))
                  .AgentRunDetailScreen,
              }),
            },
          ],
        },
        { path: '*', element: <RouteError /> },
      ],
    },
  ];
}

export function createRouter(queryClient: QueryClient) {
  return createBrowserRouter(buildRoutes(queryClient));
}
